/**
 *
 * MainPage
 *
 */

import React, { ReactElement, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import Board from 'components/Board';
import Grid from 'components/Board/Grid';
import ControlPanel from 'components/ControlPanel';
import BlockList from 'components/BlockList';
import { BoardContext } from 'store/contexts/board';
import { useContextSelector } from 'use-context-selector';
import styled from 'styled-components';

import { RULE_11, RULE_12, RULE_21 } from './shapes';

const MainPage = (): ReactElement => {
  const setBoardState = useContextSelector(
    BoardContext,
    context => context.setState,
  );

  useEffect(() => {
    setBoardState(state => ({ ...state, numOfCols: 80, numOfRows: 50 }));
  }, [setBoardState]);

  return (
    <>
      <Helmet>
        <title>Conway's Game of Life</title>
        <meta name="description" content="Conway's Game of Life Simulator" />
      </Helmet>
      <Layout>
        <BoardContainer>
          <ControlPanel />
          <Board />
          <>
            <p style={{ fontSize: '1.5em', marginBottom: '0' }}>
              Rules of Life
            </p>
            <ol style={{ marginTop: '0.25em' }}>
              <li>
                Any live cell with two or three live neighbours survives.
                <RuleGrid>
                  <Grid grid={RULE_11} />
                  <Arrow>&rarr;</Arrow>
                  <Grid grid={RULE_12} />
                </RuleGrid>
              </li>
              <li>
                Any dead cell with three live neighbours becomes a live cell.
                <RuleGrid>
                  <Grid grid={RULE_21} />
                  <Arrow>&rarr;</Arrow>
                  <Grid grid={RULE_12} />
                </RuleGrid>
              </li>
              <li>
                All other live cells die in the next generation. Similarly, all
                other dead cells stay dead.
              </li>
            </ol>
          </>
        </BoardContainer>
        <BlockList header="Select Block to Place on the Grid" />
      </Layout>
    </>
  );
};

const Layout = styled.div`
  display: flex;
  min-height: calc(100vh - 8em);
  flex-direction: row;
  justify-content: space-between;
  padding: 1.5em;
`;

const BoardContainer = styled.div`
  overflow-y: scroll;
  scrollbar-width: '-5px'; /* Firefox */
  overflow-x: scroll;
  min-height: calc(100vh - 10em);
  padding: 0 1em;

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: white;
  }

  &::-webkit-scrollbar {
    width: 8px;
    background-color: white;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: lightgray;
  }
`;

const RuleGrid = styled.div`
  display: flex;
  align-items: center;
`;

const Arrow = styled.span`
  margin: 0.5em;
  font-size: 1.5em;
`;

export default MainPage;
